import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Alert,
  Tooltip,
  Chip,
  LinearProgress,
} from '@mui/material';
import {
  Description as LogIcon,
  Refresh as RefreshIcon,
  CloudDownload as DownloadIcon,
  Visibility as ViewIcon,
  PlayArrow as TailIcon,
  Compress as CompressIcon,
} from '@mui/icons-material';

interface LogFile {
  name: string;
  size: number;
  date: string;
  isCurrent: boolean;
}

interface LogViewerWindowProps {
  websocket: WebSocket | null;
  inline?: boolean;
}

export const LogViewerWindow: React.FC<LogViewerWindowProps> = ({
  websocket,
  inline = false,
}) => {
  const [logs, setLogs] = useState<LogFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [selectedLog, setSelectedLog] = useState<LogFile | null>(null);
  const [downloadProgress, setDownloadProgress] = useState<{
    fileName: string;
    percent: number;
    bytesSent?: number;
    totalBytes?: number;
  } | null>(null);

  const pendingRequestRef = useRef<{
    type: string;
    resolve: (value: any) => void;
    reject: (error: Error) => void;
    timeoutId?: NodeJS.Timeout;
  } | null>(null);
  // Store decoded binary chunks (Uint8Array) instead of base64 strings
  // This avoids invalid base64 when concatenating padded base64 chunks
  const compressChunksRef = useRef<Uint8Array[]>([]);
  const compressFileNameRef = useRef<string>('');

  // Log directory path (configurable)
  const LOG_DIR = '/opt/comnovo/projects/comnovogateway';

  // Send SFTP command and wait for response
  const sendSftpCommand = useCallback((type: string, params: any = {}): Promise<any> => {
    return new Promise((resolve, reject) => {
      if (!websocket || websocket.readyState !== WebSocket.OPEN) {
        reject(new Error('WebSocket not connected'));
        return;
      }

      // Clear any pending request
      if (pendingRequestRef.current?.timeoutId) {
        clearTimeout(pendingRequestRef.current.timeoutId);
      }

      pendingRequestRef.current = { type, resolve, reject };

      // Initial timeout (3 minutes) - will be reset on each chunk
      const createTimeout = () => {
        return setTimeout(() => {
          if (pendingRequestRef.current?.type === type) {
            pendingRequestRef.current = null;
            reject(new Error('Operation timeout (3 minutes without progress)'));
          }
        }, 180000); // 3 minutes - reset on each chunk
      };

      const timeoutId = createTimeout();
      pendingRequestRef.current.timeoutId = timeoutId;

      // Add reset function to pending request
      (pendingRequestRef.current as any).resetTimeout = () => {
        if (pendingRequestRef.current?.timeoutId) {
          clearTimeout(pendingRequestRef.current.timeoutId);
        }
        pendingRequestRef.current!.timeoutId = createTimeout();
      };

      websocket.send(JSON.stringify({ type, ...params }));
    });
  }, [websocket]);

  // Listen to WebSocket messages for SFTP responses
  useEffect(() => {
    if (!websocket) return;

    const handleMessage = (event: MessageEvent) => {
      if (typeof event.data === 'string') {
        try {
          const message = JSON.parse(event.data);

          // Handle SFTP responses
          if (message.type === 'keepalive') {
            // Keepalive message - prevents timeout during long operations
            console.debug('Received keepalive during file operation');
          } else if (message.type === 'sftp_compress_start') {
            // Start of chunked compress/download
            compressChunksRef.current = [];
            compressFileNameRef.current = message.path.split('/').pop() || 'log';
            setDownloadProgress({
              fileName: compressFileNameRef.current,
              percent: 0,
              bytesSent: 0,
              totalBytes: message.size
            });
          } else if (message.type === 'sftp_compress_chunk') {
            // Decode base64 chunk to binary immediately
            // This avoids invalid base64 when concatenating padded chunks later
            try {
              const binaryString = atob(message.chunk);
              const bytes = new Uint8Array(binaryString.length);
              for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
              }
              compressChunksRef.current.push(bytes);
            } catch (decodeError) {
              // A dropped chunk would make the assembled file silently truncated. Fail
              // the download instead of resolving a corrupt blob. Only clear state for the
              // matching request — a stray chunk shouldn't clobber an unrelated download.
              console.error('Failed to decode chunk:', decodeError);
              if (pendingRequestRef.current?.type === 'sftp_compress') {
                if (pendingRequestRef.current.timeoutId) {
                  clearTimeout(pendingRequestRef.current.timeoutId);
                }
                pendingRequestRef.current.reject(new Error('Download failed: a chunk could not be decoded (file would be corrupt)'));
                pendingRequestRef.current = null;
                compressChunksRef.current = [];
                setDownloadProgress(null);
              }
              return;
            }
            setDownloadProgress({
              fileName: compressFileNameRef.current,
              percent: message.progress,
              bytesSent: message.bytes_sent,
              totalBytes: message.total_bytes
            });

            // Reset timeout on each chunk (we're making progress!)
            if (pendingRequestRef.current?.type === 'sftp_compress') {
              (pendingRequestRef.current as any).resetTimeout?.();
            }
          } else if (message.type === 'sftp_compress_complete') {
            // All chunks received - assemble binary data
            if (pendingRequestRef.current?.type === 'sftp_compress') {
              if (pendingRequestRef.current.timeoutId) {
                clearTimeout(pendingRequestRef.current.timeoutId);
              }

              // Concatenate all binary chunks into a single Uint8Array
              const totalLength = compressChunksRef.current.reduce((sum, chunk) => sum + chunk.length, 0);
              const fullContent = new Uint8Array(totalLength);
              let offset = 0;
              for (const chunk of compressChunksRef.current) {
                fullContent.set(chunk, offset);
                offset += chunk.length;
              }

              pendingRequestRef.current.resolve({
                binaryContent: fullContent,
                path: message.path,
                compressed_path: message.compressed_path
              });
              pendingRequestRef.current = null;

              // Clear chunks
              compressChunksRef.current = [];
            }
          } else if (message.type === 'sftp_list_result' && pendingRequestRef.current) {
            if (pendingRequestRef.current.timeoutId) {
              clearTimeout(pendingRequestRef.current.timeoutId);
            }
            pendingRequestRef.current.resolve(message);
            pendingRequestRef.current = null;
          } else if (message.type === 'error' && pendingRequestRef.current) {
            // Clear timeout
            if (pendingRequestRef.current.timeoutId) {
              clearTimeout(pendingRequestRef.current.timeoutId);
            }
            // Clear compress state
            compressChunksRef.current = [];
            setDownloadProgress(null);

            // Don't close terminal on error - just reject the promise
            pendingRequestRef.current.reject(new Error(message.message || 'Operation failed'));
            pendingRequestRef.current = null;
          }
        } catch (e) {
          // Not JSON, ignore
        }
      }
    };

    websocket.addEventListener('message', handleMessage);
    return () => websocket.removeEventListener('message', handleMessage);
  }, [websocket]);

  // Parse log filename to extract date
  const parseLogFilename = (filename: string): { date: string; isCurrent: boolean } => {
    const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD

    // Pattern: log20251016.log (dated logs)
    const match = filename.match(/log(\d{8})\.log$/);
    if (match) {
      const dateStr = match[1];
      const year = dateStr.substring(0, 4);
      const month = dateStr.substring(4, 6);
      const day = dateStr.substring(6, 8);
      const logDate = `${year}-${month}-${day}`;

      return {
        date: logDate,
        isCurrent: logDate === today, // Current if it's today's date
      };
    }

    // Fallback: comnovogateway.log (if exists)
    if (filename.includes('comnovogateway.log')) {
      return {
        date: today,
        isCurrent: true,
      };
    }

    return {
      date: 'Unknown',
      isCurrent: false,
    };
  };

  // Load log files from device
  const loadLogs = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const response = await sendSftpCommand('sftp_list', { path: LOG_DIR });

      const logFiles: LogFile[] = response.files
        .filter((f: any) =>
          !f.is_dir &&
          (f.name.match(/log\d{8}\.log$/) || f.name.includes('comnovogateway.log'))
        )
        .map((f: any) => {
          const { date, isCurrent } = parseLogFilename(f.name);
          return {
            name: f.name,
            size: f.size,
            date,
            isCurrent,
          };
        })
        .sort((a: LogFile, b: LogFile) => {
          // Current log first, then by date descending
          if (a.isCurrent) return -1;
          if (b.isCurrent) return 1;
          return b.date.localeCompare(a.date);
        });

      setLogs(logFiles);
    } catch (err: any) {
      setError(`Failed to load logs: ${err.message}`);
      console.error('Log loading error:', err);
    } finally {
      setLoading(false);
    }
  }, [sendSftpCommand]);

  // Download log (with compression and chunked transfer)
  const downloadLog = useCallback(async (log: LogFile) => {
    const fullPath = `${LOG_DIR}/${log.name}`;

    try {
      // Request compression and download - backend will send progress updates
      // Chunks are decoded to binary in the message handler
      const response = await sendSftpCommand('sftp_compress', {
        path: fullPath,
        cleanup: true  // Remove .gz file after download
      });

      // Response contains pre-decoded binary content (Uint8Array)
      const blob = new Blob([response.binaryContent], { type: 'application/gzip' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${log.name}.gz`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      // Show 100% briefly before hiding
      setDownloadProgress({ fileName: log.name, percent: 100 });
      setTimeout(() => setDownloadProgress(null), 1500);

    } catch (err: any) {
      setDownloadProgress(null);
      setError(`Download failed: ${err.message}`);
    }
  }, [sendSftpCommand]);

  // View log in terminal (send command via WebSocket, not just display)
  const viewLog = useCallback((log: LogFile) => {
    if (!websocket || websocket.readyState !== WebSocket.OPEN) return;

    const fullPath = `${LOG_DIR}/${log.name}`;
    const command = `tail -n 100 "${fullPath}"\n`;

    // Send command as bytes through WebSocket (like terminal input)
    const encoder = new TextEncoder();
    websocket.send(encoder.encode(command));
  }, [websocket]);

  // Tail log (live view - send command via WebSocket)
  const tailLog = useCallback((log: LogFile) => {
    if (!websocket || websocket.readyState !== WebSocket.OPEN) return;

    const fullPath = `${LOG_DIR}/${log.name}`;
    const command = `tail -f "${fullPath}"\n`;

    // Send command as bytes through WebSocket
    const encoder = new TextEncoder();
    websocket.send(encoder.encode(command));
  }, [websocket]);

  const formatSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  // Load logs on mount
  useEffect(() => {
    if (websocket?.readyState === WebSocket.OPEN) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- initial fetch from the device
      loadLogs();
    }
  }, [websocket, loadLogs]);

  if (!websocket) {
    return null;
  }

  const content = (
    <>
      {/* Toolbar */}
      <Paper sx={{ p: 0.5, borderRadius: 0, borderBottom: 1, borderColor: 'divider' }} elevation={0}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LogIcon sx={{ fontSize: '1rem' }} />
          <Typography variant="body2" sx={{ flexGrow: 1, fontSize: '0.75rem' }}>
            Comnovo Gateway Logs
          </Typography>
          <Tooltip title="Refresh">
            <IconButton size="small" sx={{ p: 0.25 }} onClick={loadLogs}>
              <RefreshIcon sx={{ fontSize: '1rem' }} />
            </IconButton>
          </Tooltip>
        </Box>
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            fontSize: '0.65rem',
            display: 'block',
            mt: 0.5
          }}>
          {LOG_DIR}
        </Typography>
      </Paper>

      {/* Quick info */}
      <Box sx={{ px: 0.5, py: 0.25, borderBottom: 1, borderColor: 'divider', display: 'flex', gap: 0.5, alignItems: 'center' }}>
        <Chip
          label={`${logs.length} logs`}
          size="small"
          sx={{ height: 20, fontSize: '0.65rem' }}
        />
        {logs.length > 0 && (
          <Chip
            label={`Total: ${formatSize(logs.reduce((sum, log) => sum + log.size, 0))}`}
            size="small"
            variant="outlined"
            sx={{ height: 20, fontSize: '0.65rem' }}
          />
        )}
      </Box>

      {error && <Alert severity="error" onClose={() => setError('')} sx={{ m: 1 }}>{error}</Alert>}

      {/* Download Progress */}
      {downloadProgress && (
        <Box sx={{ px: 1, py: 0.5, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <CompressIcon sx={{ fontSize: '1rem', color: 'primary.main' }} />
            <Typography variant="caption" sx={{ flex: 1, fontSize: '0.7rem' }}>
              Compressing & Downloading: {downloadProgress.fileName}
            </Typography>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 'bold' }}>
              {downloadProgress.percent}%
            </Typography>
          </Box>
          {downloadProgress.totalBytes && (
            <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary', mb: 0.5, display: 'block' }}>
              {downloadProgress.bytesSent ? formatSize(downloadProgress.bytesSent) : '0 B'} / {formatSize(downloadProgress.totalBytes)} (compressed)
            </Typography>
          )}
          <LinearProgress
            variant="determinate"
            value={downloadProgress.percent}
            sx={{ height: 4, borderRadius: 2 }}
          />
        </Box>
      )}

      {/* Log list */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={20} />
          </Box>
        ) : (
          <List dense disablePadding>
            {logs.map((log) => (
              <ListItemButton
                key={log.name}
                selected={selectedLog?.name === log.name}
                onClick={() => setSelectedLog(log)}
                sx={{
                  py: 0.25,
                  px: 0.5,
                  minHeight: 'auto',
                  '&.Mui-selected': {
                    backgroundColor: 'action.selected',
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 24 }}>
                  <LogIcon color={log.isCurrent ? 'primary' : 'action'} sx={{ fontSize: '1rem' }} />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <span>{log.date}</span>
                      {log.isCurrent && (
                        <Chip label="CURRENT" size="small" color="primary" sx={{ height: 16, fontSize: '0.6rem' }} />
                      )}
                    </Box>
                  }
                  secondary={`${formatSize(log.size)} • ${log.name}`}
                  sx={{ my: 0 }}
                  slotProps={{
                    primary: { sx: { fontSize: '0.75rem', lineHeight: 1.2 } },
                    secondary: { sx: { fontSize: '0.65rem', lineHeight: 1.2 } }
                  }} />
                <Box sx={{ display: 'flex', gap: 0.25 }}>
                  {log.isCurrent && (
                    <Tooltip title="Live tail (Ctrl+C to stop)">
                      <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); tailLog(log); }}
                        sx={{ p: 0.25 }}
                        color="primary"
                      >
                        <TailIcon sx={{ fontSize: '0.9rem' }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="View last 100 lines">
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); viewLog(log); }}
                      sx={{ p: 0.25 }}
                    >
                      <ViewIcon sx={{ fontSize: '0.9rem' }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Download (compressed)">
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); downloadLog(log); }}
                      sx={{ p: 0.25 }}
                      color="success"
                    >
                      <DownloadIcon sx={{ fontSize: '0.9rem' }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </ListItemButton>
            ))}
            {logs.length === 0 && !loading && (
              <Box sx={{ p: 1, textAlign: 'center' }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.7rem'
                  }}>
                  No log files found in {LOG_DIR}
                </Typography>
              </Box>
            )}
          </List>
        )}
      </Box>
    </>
  );

  if (inline) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
        {content}
      </Box>
    );
  }

  return content;
};
